import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Search, FileText, Landmark, BadgeCheck, ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { DashboardHero, SectionIVCard } from "@/components/blueprint/SectionIVCard";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { OnboardingChecklist } from "@/components/tour/OnboardingChecklist";
import { JourneyMap } from "@/components/JourneyMap";
import { RenewalsCard } from "@/components/RenewalsCard";
import { ContingencyCard } from "@/components/ContingencyCard";
import { useTour } from "@/components/tour/TourProvider";
import { FundingRoadmap } from "@/components/roadmap/FundingRoadmap";

const TILES = [
  { k: "saved_grants", label: "Saved grants", to: "/dashboard/grants", icon: Search, cta: "Search grants" },
  { k: "proposals", label: "Proposals", to: "/dashboard/proposals", icon: FileText, cta: "Write a proposal" },
  { k: "licensing_guides", label: "Licensing guides", to: "/dashboard/licensing", icon: Landmark, cta: "Start a guide" },
  { k: "reviews", label: "Human reviews", to: "/dashboard/reviews", icon: BadgeCheck, cta: "View reviews" },
];

export default function Dashboard() {
  const { user, hasPkg } = useAuth();
  const { openGuide } = useOutletContext();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [profile, setProfile] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [bp, setBp] = useState(null);
  const loadAll = useCallback(() => {
    api.get("/workspace").then((r) => setProfile(r.data.profile || {}));
    api.get("/readiness").then((r) => setReadiness(r.data));
    api.get("/blueprints").then((r) => setBp(r.data[0] || null));
  }, []);
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    loadAll();
  }, [loadAll]);
  const pkgs = [
    ["Proposal Writer", hasPkg("proposal_writer")],
    ["State Licensing Guide", hasPkg("licensing_guide")],
  ];
  const { lang: tourLang } = useTour();
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-xs tracking-[0.18em] uppercase text-slate-500">Home</div>
        <Button onClick={openGuide} className="bg-slate-900 hover:bg-slate-800 h-10" data-testid="dashboard-open-guide-button"><Sparkles className="h-4 w-4 mr-2 text-emerald-400" />Walk me through it</Button>
      </div>
      <FundingRoadmap />
      <RenewalsCard />
      <h2 className="font-display text-lg font-bold text-slate-900 mt-10 mb-4" data-testid="dashboard-more-heading">Your account at a glance</h2>
      {profile && <DashboardHero user={user} profile={profile} readiness={readiness} bp={bp} />}
      <SectionIVCard bp={bp} onStart={() => navigate("/dashboard/blueprint")} onUnlocked={(b) => { setBp(b); loadAll(); }} />
      <ContingencyCard />
      <div className="mb-6"><JourneyMap lang={tourLang} /></div>
      <div className="mb-6"><OnboardingChecklist lang={tourLang} hideWhenDone /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TILES.map((t, i) => (
          <Link key={t.k} to={t.to} data-testid={`stat-tile-${t.k}`} className="card-lift bg-white border border-slate-200 rounded-lg p-6 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex justify-between items-start">
              <t.icon className="h-5 w-5 text-emerald-600" />
              <ArrowUpRight className="h-4 w-4 text-slate-300" />
            </div>
            <div className="font-display text-4xl font-extrabold mt-6 text-slate-900" data-testid={`stat-value-${t.k}`}>{stats[t.k] ?? "—"}</div>
            <div className="text-sm text-slate-500 mt-1">{t.label}</div>
            <div className="text-xs font-semibold text-emerald-700 mt-4">{t.cta} →</div>
          </Link>
        ))}
      </div>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-slate-900 text-white rounded-lg p-8">
          <div className="font-mono text-xs tracking-[0.18em] uppercase text-emerald-400">Your packages</div>
          <div className="mt-6 space-y-3">
            {pkgs.map(([name, on]) => (
              <div key={name} className="flex items-center justify-between border-b border-slate-700 pb-3">
                <span>{name}</span>
                <span className={`text-xs font-mono uppercase tracking-widest ${on ? "text-emerald-400" : "text-slate-500"}`} data-testid={`package-status-${name.toLowerCase().replace(/ /g, "-")}`}>{on ? "Active" : "Locked"}</span>
              </div>
            ))}
          </div>
          <Link to="/dashboard/billing" className="inline-block mt-6 text-sm text-emerald-400 hover:underline" data-testid="dashboard-manage-plans-link">Manage plans →</Link>
        </div>
        <div className="border-l-4 border-amber-500 bg-white border border-slate-200 rounded-lg p-8">
          <BadgeCheck className="h-6 w-6 text-amber-600" />
          <h3 className="font-display text-xl font-bold mt-4 text-slate-900">Human final review</h3>
          <p className="text-sm text-slate-600 mt-2">Send any generated proposal to a certified Peer Verify editor for a compliance check — $199 flat.</p>
        </div>
      </div>
      {profile && !profile.onboarded && <OnboardingWizard profile={profile} onDone={loadAll} />}
    </div>
  );
}
